export default class GeojsonCache {
  _status = false;
  _geojson = undefined;
  _url = undefined;

  constructor(url) {
    this._url = url;
  }

  get status() {
    return this._status;
  }

  /**
   * Url de una capa remota en formato geojson
   * @param {String} url
   */
  set url(url) {
    this._url = url;
  }

  /**
   * Carga y los guarda los datos de la capa
   * en el etributo geojson de esta clase
   * @returns Boolean
   */
  async guardarGeojson() {
    await cargarGeojson(this._url).then((data) => {
      if (data.hasOwnProperty("features")) {
        this._geojson = JSON.stringify(data);
        this._status = true;
      }
    });
    return this._status;
  }

  set geojson(geojson) {
    this._status = true;
    this._geojson = JSON.stringify(geojson);
  }

  get geojson() {
    if (this._status) {
      return JSON.parse(this._geojson);
    }
    console.log("¡No se ha cargado la capa! Intenta con guardarGeojson()");
    return { features: [] };
  }

  get features() {
    return this.geojson.features;
  }

  get properties() {
    return this.features.map((f) => f.properties);
  }

  get geometrias() {
    return this.features.map((f) => f.geometry);
  }

  get vertices() {
    return this.geometrias.map(getVertices);
  }

  get limites() {
    return this.vertices.map((v) => calcularLimites(v));
  }

  get limitesTotales() {
    return calcularLimites(this.vertices.flat());
  }

  /**
   * Devuelve el geojson con los datos de la capa
   * aplicando el filtro que llega como parámetro
   * @param {Function} filtro
   * @returns
   */
  geojsonFiltro(filtro, calcularBbox = false) {
    if (typeof filtro === "function") {
      const _geojson = { ...this.geojson };
      _geojson.features = _geojson.features.filter(filtro);

      if (calcularBbox) {
        _geojson.bbox = calcularLimites(
          _geojson.features
            .map((f) => f.geometry)
            .map(getVertices)
            .flat()
        );
      }

      return _geojson;
    }

    if (filtro !== undefined) {
      console.warn("¡No es posible aplicar el filtro!");
    }
    return this.geojson;
  }

  featuresFiltro(filtro) {
    return this.geojsonFiltro(filtro).features;
  }

  propertiesFiltro(filtro) {
    return this.featuresFiltro(filtro).map((f) => f.properties);
  }

  geometriasFiltro(filtro) {
    return this.featuresFiltro(filtro).map((f) => f.geometry);
  }

  verticesFiltro(filtro) {
    return this.geometriasFiltro(filtro).map(getVertices);
  }

  limitesFiltro(filtro) {
    return this.verticesFiltro(filtro).map((v) => calcularLimites(v));
  }

  limitesTotalesFiltro(filtro) {
    return calcularLimites(this.verticesFiltro(filtro).flat());
  }
}

const getVertices = (g) => {
  // return g.coordinates.flat(tiposGeometria[g.type].flat);
  const _vertices = g.coordinates.flat(g.type.toLowerCase().includes("multi") ? 2 : 1);
  return g.type === "Point" ? [_vertices] : _vertices;
};

function MultiGeometriaAsWKT(geometria) {
  return `${geometria.type.toUpperCase()} (${geometria.coordinates
    .map(
      (multi) =>
        `(${multi.map((geom) => `(${geom.map((xy) => xy.join(" ")).join(", ")})`).join(", ")})`
    )
    .join(", ")})`;
}

/**
 * Consulta los datos de una capa remota en formato geojson
 * @returns Promise
 */
export async function cargarGeojson(url) {
  const respuesta = await fetch(url);
  if (respuesta.ok) return respuesta.json();
  else {
    console.info(respuesta);
    return { features: [] };
  }
}

export function calcularLimites(vertices) {
  // xmin, ymin, xmax, ymax
  return [
    Math.min(...vertices.map((vertice) => vertice[0])),
    Math.min(...vertices.map((vertice) => vertice[1])),
    Math.max(...vertices.map((vertice) => vertice[0])),
    Math.max(...vertices.map((vertice) => vertice[1])),
  ];
}

export const tiposGeometria = {
  MultiPolygon: {
    flat: 2,
  },
};
